import { onClickProductCard, onLoadAndResizeProductCard, checkModalCart } from './modal-cart';
import { btnAddToCart } from './cart';
import { collectionMoreInfo } from './collection';

let mobileFiltersScrollY = 0;
let mobileFiltersScrollLocked = false;

const getHeaderBottomPosition = () => {
  const headerPosition = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue("--header-bottom-position"),
    10
  );

  if (Number.isFinite(headerPosition)) return headerPosition;

  return Math.max(0, document.querySelector(".header")?.getBoundingClientRect().bottom || 0);
};

const updateMobileFiltersSticky = () => {
  const root = document.querySelector("[data-mobile-filters-sticky]");
  const bar = root?.querySelector("[data-mobile-filters-bar]");

  if (!root || !bar) return;

  if (window.innerWidth >= 992) {
    root.classList.remove("is-fixed");
    root.style.removeProperty("--mobile-filters-height");
    return;
  }

  const headerBottom = getHeaderBottomPosition();
  const barHeight = bar.offsetHeight;
  const gridBottom = document.getElementById("ProductGridContainer")?.getBoundingClientRect().bottom || 0;
  const shouldStick = root.getBoundingClientRect().top <= headerBottom && gridBottom > headerBottom + barHeight;

  root.style.setProperty("--mobile-filters-height", `${barHeight}px`);
  root.classList.toggle("is-fixed", shouldStick);
};

const initMobileFiltersSticky = () => {
  if (FacetFiltersForm.mobileFiltersStickyInitialized) {
    updateMobileFiltersSticky();
    return;
  }

  window.addEventListener("scroll", updateMobileFiltersSticky, { passive: true });
  window.addEventListener("resize", updateMobileFiltersSticky);
  document.addEventListener("facet_updated", updateMobileFiltersSticky);

  FacetFiltersForm.mobileFiltersStickyInitialized = true;
  updateMobileFiltersSticky();
};

const setMobileFiltersScrollLock = (locked = false) => {
  const shouldLock = locked && window.innerWidth < 992;
  const html = document.documentElement;
  const body = document.body;

  if (shouldLock === mobileFiltersScrollLocked) return;

  if (shouldLock) {
    mobileFiltersScrollY = window.scrollY || html.scrollTop || 0;
    html.classList.add("collection-mobile-filters-open");
    body.classList.add("collection-mobile-filters-open");
    body.style.position = "fixed";
    body.style.top = `-${mobileFiltersScrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  } else {
    const restoreScrollY = mobileFiltersScrollY;

    html.classList.remove("collection-mobile-filters-open");
    body.classList.remove("collection-mobile-filters-open");
    body.style.removeProperty("position");
    body.style.removeProperty("top");
    body.style.removeProperty("left");
    body.style.removeProperty("right");
    body.style.removeProperty("width");

    if (mobileFiltersScrollLocked) {
      window.scrollTo(0, restoreScrollY);
    }
  }

  mobileFiltersScrollLocked = shouldLock;
};

const syncMobileFiltersModalState = () => {
  const toggle = document.querySelector("[data-mobile-filters-toggle]");
  setMobileFiltersScrollLock(Boolean(toggle?.checked));
};

const hasActiveMobileFilters = () => {
  const form = document.querySelector("[data-filters-modal]");
  if (!form) return false;

  if (form.querySelector('input[name^="filter."][type="checkbox"]:checked')) {
    return true;
  }

  const priceInputs = form.querySelectorAll('input[name^="filter."][type="number"]');
  for (const input of priceInputs) {
    const value = Number(input.value);
    if (!Number.isFinite(value)) continue;

    const min = Number(input.getAttribute("min") || 0);
    const max = Number(input.getAttribute("max"));
    const isMinInput = input.name.includes(".gte") || input.id.toLowerCase().includes("from");
    const isMaxInput = input.name.includes(".lte") || input.id.toLowerCase().includes("to");

    if (isMinInput && value > min) return true;
    if (isMaxInput && Number.isFinite(max) && value < max) return true;
  }

  const activeSort = form.querySelector('input[name="sort_by"]:checked');
  return Boolean(activeSort?.value);
};

const updateMobileFiltersActiveDot = () => {
  const dot = document.querySelector("[data-mobile-filters-active-dot]");
  if (!dot) return;

  dot.classList.toggle("jc-hidden", !hasActiveMobileFilters());
};

const initMobileFiltersModal = () => {
  if (!FacetFiltersForm.mobileFiltersModalInitialized) {
    document.addEventListener("change", (event) => {
      if (!event.target.matches("[data-mobile-filters-toggle]")) return;

      setMobileFiltersScrollLock(event.target.checked);
    });

    document.addEventListener("change", (event) => {
      if (!event.target.closest("[data-filters-modal]")) return;

      updateMobileFiltersActiveDot();
    });

    document.addEventListener("input", (event) => {
      if (!event.target.closest("[data-filters-modal]")) return;

      updateMobileFiltersActiveDot();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;

      const toggle = document.querySelector("[data-mobile-filters-toggle]:checked");
      if (!toggle) return;

      toggle.checked = false;
      setMobileFiltersScrollLock(false);
    });

    window.addEventListener("resize", syncMobileFiltersModalState);
    FacetFiltersForm.mobileFiltersModalInitialized = true;
  }

  syncMobileFiltersModalState();
  updateMobileFiltersActiveDot();
};

export default class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);

    const form = this.querySelector("form");

    form?.addEventListener("change", (e) => {
      e.preventDefault();
      this.onSubmitHandler(e);
    })

    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      this.onSubmitHandler(e);
    })

    initMobileFiltersSticky();
    initMobileFiltersModal();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static closeCurrentPopup() {
    const currentPopup = document.querySelector("g-modal[aria-expanded='true']");
    if (!currentPopup) return;

    currentPopup.close();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static getSections() {
    return [
      {
        section: document.getElementById("ProductGridContainer")?.dataset.id ||
          document.getElementById("product-grid")?.dataset.id,
      },
    ];
  }

  static toggleLoading(enable = false) {
    const loadContainer = document.getElementById("loadContainer");
    const facetsControls = document.querySelectorAll("[data-facets-submit]");

    loadContainer?.setAttribute("aria-busy", enable.toString());

    facetsControls.forEach((btn) => {
      btn.setAttribute("aria-busy", enable.toString());
    });

    const event = new CustomEvent("facet_updated");
    document.dispatchEvent(event);
  }

  static renderSectionFromFetch(url) {
    FacetFiltersForm.toggleLoading(true);

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.updateFiltersHTML(html);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.hydrateProductGrid();
        FacetFiltersForm.toggleLoading(false);
        FacetFiltersForm.closeCurrentPopup();
      });
  }

  static renderSectionFromCache(filterDataUrl) {
    FacetFiltersForm.toggleLoading(true);
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.updateFiltersHTML(html);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.hydrateProductGrid();
    FacetFiltersForm.toggleLoading(false);
    FacetFiltersForm.closeCurrentPopup();
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").outerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").outerHTML;
  }

  static hydrateProductGrid() {
    const productGridContainer = document.getElementById("ProductGridContainer");

    productGridContainer
      ?.querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });

    setTimeout(() => {
      onClickProductCard();
      onLoadAndResizeProductCard();
    }, 0);

    btnAddToCart(".add-product-cart");
    btnAddToCart(".add-product-cart-upsell");
    collectionMoreInfo("#collection__button");
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, "text/html").getElementById("ProductCount")?.innerHTML;
    const container = document.getElementById("ProductCount");
    if (!count || !container) return;

    container.innerHTML = count;
  }

  static updateFiltersHTML(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const inlineFiltersOpen = FacetFiltersForm.getInlineFiltersOpenState();
    const selectors = [
      "[data-filters-modal]",
      "[data-filters-aside]",
      "[data-filters-sort]",
      "[data-facets-remove]",
      "[data-filter-trigger]",
      "[data-filters-inline]"
    ];

    selectors.forEach((selector) => {
      const newElements = parsedHTML.querySelectorAll(selector);
      const currentElements = document.querySelectorAll(selector);
      if (newElements.length === 0 || currentElements.length === 0) return;

      currentElements.forEach((currentElement) => {
        newElements.forEach((newElement) => {
          currentElement.innerHTML = newElement.innerHTML;
        });
      });
    });

    FacetFiltersForm.setInlineFiltersOpenState(inlineFiltersOpen);
    initMobileFiltersSticky();
    initMobileFiltersModal();
  }

  static getInlineFiltersOpenState() {
    const inlineFiltersToggle = document.querySelector("[data-filters-inline] .js-accordion-input");
    return Boolean(inlineFiltersToggle?.checked);
  }

  static setInlineFiltersOpenState(isOpen) {
    const inlineFiltersToggle = document.querySelector("[data-filters-inline] .js-accordion-input");
    if (!inlineFiltersToggle) return;

    inlineFiltersToggle.checked = isOpen;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    // Create a new FormData without the excluded inputs
    const filteredFormData = new FormData();

    for (const [key, value] of formData.entries()) {
      const inputElement = form.querySelector(`[name="${key}"]`);

      if (inputElement && !inputElement.classList.contains("js-accordion-input")) {
        filteredFormData.append(key, value);
      }
    }

    return new URLSearchParams(filteredFormData).toString();
  }

  onSubmitHandler(event) {
    event.preventDefault()

    // Ignore changes from accordion inputs
    if (event.target.classList.contains('js-accordion-input')) {
      return;
    }

    const forms = [];
    const targetForm = event.target.closest('form');

    // Ignore input changes from modal form
    if (targetForm && ["FacetFiltersFormModal"].includes(targetForm.id) && event.type === 'change') {
      return;
    }

    if (targetForm?.id === "FacetFiltersFormModal") {
      setMobileFiltersScrollLock(false);
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    sortFilterForms.forEach((form) => {
      const isSorting = targetForm?.id === 'FacetSortForm' || form?.id === 'FacetSortForm';
      if (["FacetFiltersForm", "FacetSortForm", "FacetFiltersFormModal"].includes(form.id) && (form === targetForm || isSorting)) {
        if (!forms.includes(this.createSearchParams(form))) {
          forms.push(this.createSearchParams(form));
        }
      }
    });

    FacetFiltersForm.renderPage(forms.join("&"), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.mobileFiltersStickyInitialized = false;
FacetFiltersForm.mobileFiltersModalInitialized = false;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();
initMobileFiltersSticky();
initMobileFiltersModal();

setTimeout(() => {
  checkModalCart();
}, 2000);


class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const form = this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");

    if (!form) {
      console.error("Facet filter form not found!");
      return;
    }

    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-filters-remove", FacetRemove);

// Used for price range filter
class RangeSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.fromSlider = this.querySelector("#fromSlider");
    this.toSlider = this.querySelector("#toSlider");
    this.fromInput = this.querySelector("#fromInput");
    this.toInput = this.querySelector("#toInput");

    if (!this.fromSlider || !this.toSlider || !this.fromInput || !this.toInput) return;

    this.min = this.getNumberAttribute("min", 0);
    this.max = this.getNumberAttribute("max", 100);
    this.gap = Math.max(0, Math.min(this.getNumberAttribute("gap", 5), this.max - this.min));
    this.from = this.clamp(this.getNumberAttribute("from", this.min), this.min, this.max);
    this.to = this.clamp(this.getNumberAttribute("to", this.max), this.min, this.max);

    this.normalizeRangeValues();

    this.fromSlider.min = this.min;
    this.fromSlider.max = this.max;
    this.fromSlider.value = this.from;

    this.toSlider.min = this.min;
    this.toSlider.max = this.max;
    this.toSlider.value = this.to;

    this.fromInput.min = this.min;
    this.fromInput.max = this.max;
    this.fromInput.value = this.from;

    this.toInput.min = this.min;
    this.toInput.max = this.max;
    this.toInput.value = this.to;

    this.fillSlider();
    this.setToggleAccessible();

    this.fromSlider.addEventListener("input", () => this.controlFromSlider());
    this.toSlider.addEventListener("input", () => this.controlToSlider());

    this.fromInput.addEventListener("input", () => this.controlFromInput());
    this.toInput.addEventListener("input", () => this.controlToInput());

    this.fromInput.addEventListener("change", () => {
      this.controlFromInput(true);
    });

    this.toInput.addEventListener("change", () => {
      this.controlToInput(true);
    });

    this.fromSlider.addEventListener("change", (event) => {
      event.stopPropagation();
      this.controlFromSlider();
      this.dispatchNativeChange(this.fromInput);
    });

    this.toSlider.addEventListener("change", (event) => {
      event.stopPropagation();
      this.controlToSlider();
      this.dispatchNativeChange(this.toInput);
    });
  }

  getNumberAttribute(attributeName, fallback) {
    if (!this.hasAttribute(attributeName) || this.getAttribute(attributeName) === "") return fallback;

    const value = Number(this.getAttribute(attributeName));
    return Number.isFinite(value) ? value : fallback;
  }

  getNumberValue(input, fallback) {
    const value = Number(input.value);
    return Number.isFinite(value) ? value : fallback;
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  normalizeRangeValues() {
    if (this.max <= this.min) {
      this.max = this.min;
      this.gap = 0;
      this.from = this.min;
      this.to = this.max;
      return;
    }

    if (this.to - this.from >= this.gap) return;

    if (this.to === this.max) {
      this.from = this.clamp(this.to - this.gap, this.min, this.max);
    } else {
      this.to = this.clamp(this.from + this.gap, this.min, this.max);
    }
  }

  controlFromSlider() {
    let from = this.getNumberValue(this.fromSlider, this.min);
    const to = this.getNumberValue(this.toSlider, this.max);

    if (from + this.gap > to) {
      from = to - this.gap;
      this.fromSlider.value = from;
    }

    from = this.clamp(from, this.min, this.max);
    this.fromSlider.value = from;
    this.fromInput.value = from;
    this.fillSlider();
    this.setToggleAccessible();
  }

  controlToSlider() {
    const from = this.getNumberValue(this.fromSlider, this.min);
    let to = this.getNumberValue(this.toSlider, this.max);

    if (from + this.gap > to) {
      to = from + this.gap;
      this.toSlider.value = to;
    }

    to = this.clamp(to, this.min, this.max);
    this.toSlider.value = to;
    this.toInput.value = to;
    this.fillSlider();
    this.setToggleAccessible();
  }

  controlFromInput(forceValue = false) {
    if (this.fromInput.value === "" && !forceValue) return;

    let from = this.getNumberValue(this.fromInput, this.min);
    const to = this.getNumberValue(this.toInput, this.max);

    from = this.clamp(from, this.min, this.max);
    if (from + this.gap > to) from = to - this.gap;
    from = this.clamp(from, this.min, this.max);

    this.fromInput.value = from;
    this.fromSlider.value = from;
    this.fillSlider();
    this.setToggleAccessible();
  }

  controlToInput(forceValue = false) {
    if (this.toInput.value === "" && !forceValue) return;

    const from = this.getNumberValue(this.fromInput, this.min);
    let to = this.getNumberValue(this.toInput, this.max);

    to = this.clamp(to, this.min, this.max);
    if (to < from + this.gap) to = from + this.gap;
    to = this.clamp(to, this.min, this.max);

    this.toInput.value = to;
    this.toSlider.value = to;
    this.fillSlider();
    this.setToggleAccessible();
  }

  fillSlider() {
    this.fromSlider.style.background = "transparent";
    this.toSlider.style.background = "transparent";
  }

  setToggleAccessible() {
    if (Number(this.fromSlider.value) > this.max - this.gap) {
      this.fromSlider.style.zIndex = 3;
    } else {
      this.fromSlider.style.zIndex = 2;
    }
  }

  dispatchNativeChange(inputElement) {
    inputElement.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

customElements.define("range-slider", RangeSlider);
